import type { ErrorHandler, ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import { auth } from '@frameio/utils/auth';
import { isServer, isWorker } from '@frameio/utils/config';
import { isUXPEnvironment } from '@frameio/utils/config/helpers';
import {
  GRAPHQL_ERROR_CODES,
  GRAPHQL_ERROR_MESSAGES,
} from '../../enums/graphqlErrors';

const handleGraphQLError = (graphqlError, operation) => {
  const { message, extensions } = graphqlError;

  const { UNAUTHENTICATED } = GRAPHQL_ERROR_CODES;
  const {
    INVALID_SHARE_PASSPHRASE,
    INVALID_UPLOAD_LINK_PASSPHRASE,
    INVALID_SHARE,
    UNCONFIRMED_EMAIL,
  } = GRAPHQL_ERROR_MESSAGES;

  // We send more than one type of error using the UNAUTHENTICATED code
  if (extensions?.code === UNAUTHENTICATED) {
    switch (message) {
      case INVALID_SHARE_PASSPHRASE:
        if (
          operation.getContext().shouldIgnoreInvalidSharePassphraseError ===
          true
        )
          return;
        if (typeof window === 'undefined') {
          // Warn for future debugging if client links are used in the server
          // eslint-disable-next-line no-console
          console.warn('Unhandled error case: INVALID_SHARE_PASSPHRASE');
          return;
        }
        /**
         * The user's share credentials are no longer valid. This happens when
         * the share they are viewing has a passphrase added or changed.
         * We reload the page to force the Share Access Passphrase error page
         */
        window.location.reload();
        return;
      case INVALID_UPLOAD_LINK_PASSPHRASE:
        if (
          operation.getContext()
            .shouldIgnoreInvalidUploadLinkPassphraseError === true
        )
          return;
        if (typeof window === 'undefined') {
          // Warn for future debugging if client links are used in the server
          // eslint-disable-next-line no-console
          console.warn('Unhandled error case: INVALID_UPLOAD_LINK_PASSPHRASE');
          return;
        }
        /**
         * The visitor's upload-link passphrase is no longer valid (added or
         * changed while they were on the page). Reload to force the upload-link
         * passphrase gate.
         */
        window.location.reload();
        return;
      case UNCONFIRMED_EMAIL:
        return;
      default:
        // We can't redirect from the web worker, but the next request from the
        // main thread will cause this same error and the user will be redirected.
        if (isWorker) {
          return;
        }
        // We don't have the request context here in order to redirect the user.
        // This error is handled by catch blocks in server components
        if (isServer) {
          return;
        }

        /**
         * Don't redirect if this request returns unauthenticated because this
         * error is expected when visiting certain Share pages.
         * see V4BG-822
         */
        if (
          operation.getContext().shouldRedirectToLoginIfUnauthenticated ===
          false
        ) {
          return;
        }
        if (!isUXPEnvironment()) {
          auth.redirectToAuthRefresh();
        }
    }
  }

  switch (message) {
    case INVALID_SHARE:
      /**
       * The share became invalid while viewing it
       * We reload the page to force the Share Unavailable page (404 not found)
       */
      if (typeof window !== 'undefined') {
        window.location.reload();
      } else {
        // Warn for future debugging if client links are used in the server
        // eslint-disable-next-line no-console
        console.warn('Unhandled error case: INVALID_SHARE');
      }
      return;
    default:
      // eslint-disable-next-line no-case-declarations
      const errorMessage = `GraphQL Error (${operation.operationName}): ${graphqlError.message}`;
      // eslint-disable-next-line no-case-declarations
      const formattedError = new Error(errorMessage);

      /* eslint-disable no-console */
      console.groupCollapsed(
        `%c${formattedError}`,
        'color: red; font-weight: bold;'
      );
      console.log(`Message: ${message}`);
      if (extensions?.code) console.log(`Code: ${extensions.code}`);
      console.log(operation);
      console.groupEnd();
    /* eslint-enable no-console */
  }
};

const handleNetworkError = (networkError, operation) => {
  /* eslint-disable no-console */
  console.groupCollapsed(
    `%cNetwork Error: ${operation.operationName}`,
    'color: red; font-weight: bold;'
  );
  console.log(operation);
  console.error(networkError);
  console.groupEnd();
  /* eslint-enable no-console */
};

const handleApolloError: ErrorHandler = ({
  operation,
  graphQLErrors,
  networkError,
}: ErrorResponse) => {
  if (graphQLErrors && graphQLErrors.length)
    graphQLErrors.forEach((graphqlError) =>
      handleGraphQLError(graphqlError, operation)
    );
  if (networkError) handleNetworkError(networkError, operation);
};

// global error handler (client + server)
export default onError(handleApolloError);
